import { useId } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { Button, Flex, Popover, Tooltip } from '@buffer-mono/popcorn'

import {
  DateRangePickerContent,
  DateRangePickerDateInputs,
  useDateRangePickerState,
} from './DateRangePickerPanel'

import styles from './DateRangePopover.module.css'

interface DateRangePopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialRange?: { startDate: Date; endDate: Date }
  onApply: (startDate: Date, endDate: Date) => void
  weekStartsOn?: 0 | 1
  children: ReactNode
  /**
   * When true, the anchor element does not auto-open the popover on click —
   * the caller controls `open` entirely via `onOpenChange`. Useful when the
   * anchor doubles as a trigger for another menu.
   */
  manualTrigger?: boolean
}

export function DateRangePopover({
  open,
  onOpenChange,
  initialRange,
  onApply,
  weekStartsOn,
  children,
  manualTrigger = false,
}: DateRangePopoverProps): JSX.Element {
  const state = useDateRangePickerState({
    open,
    onOpenChange,
    initialRange,
    onApply,
    weekStartsOn,
  })

  const contentId = useId()

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Anchor>
        {manualTrigger ? (
          <span aria-controls={contentId}>{children}</span>
        ) : (
          <span
            // The caller provides arbitrary trigger content, which can itself contain a button.
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-controls={contentId}
            onClick={() => onOpenChange(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpenChange(true)
              }
            }}
          >
            {children}
          </span>
        )}
      </Popover.Anchor>
      <Popover.Content id={contentId} align="start" className={styles.content}>
        <div className={styles.body}>
          <DateRangePickerContent
            state={state}
            weekStartsOn={weekStartsOn}
            numberOfMonths={2}
          />
        </div>
        <div className={styles.footer}>
          <DateRangePickerDateInputs state={state} />
          <Flex gap="xs">
            <Button
              variant="tertiary"
              size="medium"
              onClick={state.handleCancel}
            >
              Cancel
            </Button>
            <Tooltip
              content={state.applyDisabledReason}
              open={state.applyDisabledReason ? undefined : false}
            >
              <Button
                variant="primary"
                size="medium"
                onClick={state.handleApply}
                disabled={state.isApplyDisabled}
              >
                Apply
              </Button>
            </Tooltip>
          </Flex>
        </div>
      </Popover.Content>
    </Popover>
  )
}
